import React, { useState, useEffect } from 'react';
import { X, Info, Check, Shield } from 'lucide-react';

const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Vérifier si le consentement a déjà été donné
    const consentGiven = localStorage.getItem('cookieConsent');
    if (!consentGiven) {
      // Afficher la bannière après un court délai pour une meilleure expérience utilisateur
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    // Stocker le consentement avec la date
    localStorage.setItem('cookieConsent', JSON.stringify({
      accepted: true,
      date: new Date().toISOString(),
      version: '1.0'
    }));
    setIsVisible(false);
  };

  const refuseCookies = () => {
    // Stocker le refus avec la date
    localStorage.setItem('cookieConsent', JSON.stringify({
      accepted: false,
      date: new Date().toISOString(),
      version: '1.0'
    }));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-lg border-t-2 border-[#9a6ebd]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Bannière principale */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Shield className="text-[#9a6ebd] flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="text-lg font-semibold text-[#323c5f] mb-1">
                Nous respectons votre vie privée
              </h3>
              <p className="text-sm text-gray-600 max-w-3xl">
                Ce site utilise des cookies pour améliorer votre expérience, analyser le trafic et personnaliser le contenu. 
                En cliquant sur "Accepter", vous consentez à notre utilisation des cookies conformément à notre politique de confidentialité.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 ml-0 md:ml-4">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-[#9a6ebd] border border-[#9a6ebd] px-4 py-2 rounded-lg hover:bg-[#9a6ebd]/5 transition-colors text-sm font-medium flex items-center justify-center"
            >
              <Info size={16} className="mr-2" />
              En savoir plus
            </button>
            <button
              onClick={refuseCookies}
              className="text-gray-700 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              Refuser
            </button>
            <button
              onClick={acceptCookies}
              className="bg-[#9a6ebd] text-white px-4 py-2 rounded-lg hover:bg-[#8a5dad] transition-colors text-sm font-medium flex items-center justify-center"
            >
              <Check size={16} className="mr-2" />
              Accepter
            </button>
          </div>
        </div>

        {/* Détails des cookies */}
        {showDetails && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-[#323c5f] mb-2">Cookies nécessaires</h4>
                <p className="text-sm text-gray-600">
                  Ces cookies sont indispensables au fonctionnement du site et ne peuvent pas être désactivés.
                  Ils ne stockent aucune information personnelle identifiable.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-[#323c5f] mb-2">Cookies analytiques</h4>
                <p className="text-sm text-gray-600">
                  Ces cookies nous permettent d'analyser l'utilisation du site afin d'en améliorer les performances et l'expérience utilisateur.
                  Toutes les informations collectées sont anonymes.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-[#323c5f] mb-2">Cookies marketing</h4>
                <p className="text-sm text-gray-600">
                  Ces cookies sont utilisés pour suivre les visiteurs sur les sites web. L'objectif est d'afficher des publicités pertinentes
                  et engageantes pour l'utilisateur.
                </p>
              </div>
            </div>
            <div className="mt-4 text-sm text-gray-600">
              <p>
                Pour plus d'informations, veuillez consulter notre{' '}
                <a href="/confidentialite" className="text-[#9a6ebd] hover:underline">
                  politique de confidentialité
                </a>{' '}
                et nos{' '}
                <a href="/mentions-legales" className="text-[#9a6ebd] hover:underline">
                  mentions légales
                </a>.
              </p>
            </div>
          </div>
        )}

        {/* Bouton de fermeture */}
        <button
          onClick={() => setIsVisible(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Fermer"
        >
          <X size={20} />
        </button>
      </div>
    </div>
  );
};

export default CookieConsent;